import { alpha, Theme } from "@mui/material/styles";

// Formatting and colour helpers shared by the Projects cards, table and detail form.

// "Close & create reference" wizard. Switched off for now - references are created manually in the
// References view. Turning it back on also needs PROJECT_REFERENCE_CONVERSION_ENABLED=true in the API .env.
export const REFERENCE_CONVERSION_ENABLED = false;

export const formatMoney = (value: number | null | undefined, currency?: string | null): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    const amount = Number(value);
    const abs = Math.abs(amount);
    const text = abs >= 1_000_000 ? `${(amount / 1_000_000).toFixed(2)} Mio`
        : abs >= 1_000 ? `${Math.round(amount / 1_000)} k`
        : amount.toFixed(0);
    return currency ? `${text} ${currency}` : text;
};

export const formatMoneyExact = (value: number | null | undefined, currency?: string | null): string => {
    if (value === null || value === undefined) return '-';
    const text = Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${text} ${currency}` : text;
};

export const formatPercent = (ratio: number | null | undefined, digits = 0): string =>
    ratio === null || ratio === undefined ? '-' : `${(Number(ratio) * 100).toFixed(digits)} %`;

// ISO/date-time string -> YYYY-MM-DD (for <input type="date"> and display).
export const toDateInput = (value: string | null | undefined): string => (value ? String(value).slice(0, 10) : '');

export const formatDate = (value: string | null | undefined): string => {
    const iso = toDateInput(value);
    if (!iso) return '-';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
};

export const splitList = (value: string | null | undefined): string[] =>
    value ? Array.from(new Set(value.split(/,\s*/).map((part) => part.trim()).filter(Boolean))) : [];

export type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const statusColor = (status: string | null | undefined): ChipColor => {
    switch (status) {
        case 'Ongoing': return 'success';
        case 'Yet to start': return 'info';
        case 'On hold': return 'warning';
        case 'Closed': return 'default';
        default: return 'default';
    }
};

// Condition drives the coloured stripe on top of each card, so problem projects stand out.
export const conditionColor = (condition: string | null | undefined): string => {
    switch (condition) {
        case 'Normal': return '#2e7d32';
        case 'Escalated': return '#d32f2f';
        case 'Dormant / Sleeping': return '#9e9e9e';
        case 'Passive / TL managed': return '#0288d1';
        case 'Extension applied for': return '#ed6c02';
        case 'Finance yet to raise final invoice':
        case 'Final Invoice not paid': return '#f9a825';
        default: return '#bdbdbd';
    }
};

// Converts empty strings from form inputs to null for the API.
export const blankToNull = <T,>(value: T | ''): T | null => (value === '' || value === undefined ? null : value);

export const numberOrNull = (value: unknown): number | null => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

// Plain-language wording for conditions on the cards; null = normal, nothing to show.
export const conditionLabel = (condition: string | null | undefined): string | null => {
    switch (condition) {
        case null: case undefined: case '': case 'Normal': return null;
        case 'Escalated': return 'Escalated';
        case 'Passive / TL managed': return 'Run by team leader';
        case 'Dormant / Sleeping': return 'Paused';
        case 'Extension applied for': return 'Extension requested';
        case 'Finance yet to raise final invoice': return 'Final invoice due';
        case 'Final Invoice not paid': return 'Waiting for payment';
        default: return condition;
    }
};

const durationText = (months: number): string => {
    if (months >= 24) return `${Math.round(months / 12)} years`;
    return months === 1 ? '1 month' : `${months} months`;
};

type HeadlineInput = {
    status: string | null;
    months_remaining: number | null;
    completion_ratio: number | null;
    time_elapsed_ratio: number | null;
    int_open_volume: number | null;
    contract_volume: number | null;
    currency: string | null;
    project_end_date: string | null;
    effective_start_date: string | null;
};

// One sentence that tells a normal user where the project stands, e.g.
// "Ends in 7 months. 48 % invoiced while 72 % of the time has passed · 1.08 Mio EUR still open."
export const projectHeadline = (item: HeadlineInput): { lead: string; rest: string } => {
    const invoiced = item.completion_ratio;
    const elapsed = item.time_elapsed_ratio;
    const openVolume = item.int_open_volume;

    if (item.status === 'Yet to start') {
        const starts = item.effective_start_date ? `Starts ${formatDate(item.effective_start_date)}` : 'Not started yet';
        return { lead: starts, rest: item.contract_volume ? ` · ${formatMoney(item.contract_volume, item.currency)} contract volume.` : '.' };
    }
    if (item.status === 'Closed') {
        const closed = item.project_end_date ? `Closed ${formatDate(item.project_end_date)}` : 'Closed';
        return { lead: closed, rest: invoiced !== null ? `. ${formatPercent(invoiced)} of the contract volume invoiced.` : '.' };
    }
    if (item.status !== 'Ongoing' && item.status !== 'On hold') {
        return { lead: item.status ? `Tender history: ${item.status.replace(/_/g, ' ')}` : 'No status', rest: '.' };
    }

    const months = item.months_remaining;
    const lead = months === null ? 'No end date'
        : months > 0 ? `Ends in ${durationText(months)}`
        : months === 0 ? 'Ends this month'
        : `End date passed ${durationText(-months)} ago`;

    const parts: string[] = [];
    if (invoiced !== null && elapsed !== null) parts.push(`${formatPercent(invoiced)} invoiced while ${formatPercent(elapsed)} of the time has passed`);
    else if (invoiced !== null) parts.push(`${formatPercent(invoiced)} invoiced`);
    if (openVolume !== null) parts.push(openVolume > 0 ? `${formatMoney(openVolume, item.currency)} still open` : 'fully invoiced');
    return { lead, rest: parts.length ? `. ${parts.join(' · ')}.` : '.' };
};

// "Ulrich Frings" -> "UF", "Imran" -> "IM"
export const initials = (name: string): string => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '?';
    return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[words.length - 1][0]).toUpperCase();
};

// Shared look of every white surface on the Projects page (KPI tiles, filter bar, cards, table,
// timeline): explicit border + soft shadow, and no theme margin so all edges line up.
export const panelSx = (theme: Theme) => ({
    margin: 0,
    borderRadius: 3,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
    boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}, 0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
});
